import classnames from 'classnames'
import { format } from 'date-fns-tz'
import React, { useState, useRef, useEffect } from 'react'
import { Text } from 'marketing-site/src/library/elements/Text'
import { CTATheme, Icon, renderIconSVG } from 'marketing-site/src/library/utils'
import { CTALink } from 'marketing-site/src/library/elements/CTALink'
import { ImageOrAutoplayVideo } from 'marketing-site/src/library/elements/ImageOrAutoplayVideo'
import { IProps } from '.'
import useTranslate from 'marketing-site/src/library/hooks/useTranslate'
import styles from './styles.scss'

export const Webinar = ({ webinarItem, handleClick }: IProps) => {
  const { translate } = useTranslate()
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const webinarRef = useRef<HTMLDivElement | null>(null)
  const bodyWrapperRef = useRef<HTMLDivElement | null>(null)

  const bodyWrapperClassNames = classnames('webinar__body')

  useEffect(() => {
    let timeout: any

    const transitionEndHandler = (event: TransitionEvent) => {
      if (event.propertyName === 'max-height') {
        if (bodyWrapperRef.current) {
          bodyWrapperRef.current.style.maxHeight = ''
        }
      }
    }

    if (bodyWrapperRef.current) {
      bodyWrapperRef.current.addEventListener('transitionend', transitionEndHandler)
    }

    if (isDirty && timeout !== undefined) {
      clearTimeout(timeout)
    }

    if (webinarItem.expanded) {
      if (!isDirty) {
        setIsDirty(true)
      }

      if (webinarRef.current) {
        webinarRef.current.classList.add('is-expanded')
      }

      if (bodyWrapperRef.current) {
        bodyWrapperRef.current.style.maxHeight = '0'
      }

      timeout = setTimeout(() => {
        if (bodyWrapperRef.current) {
          bodyWrapperRef.current.style.maxHeight = `${
            bodyWrapperRef.current.children[0].getBoundingClientRect().height + 36
          }px`
        }
      }, 10)
    } else {
      if (isDirty) {
        if (bodyWrapperRef.current) {
          bodyWrapperRef.current.style.maxHeight = `${
            bodyWrapperRef.current.children[0].getBoundingClientRect().height + 36
          }px`
        }

        timeout = setTimeout(() => {
          if (webinarRef.current) {
            webinarRef.current.classList.remove('is-expanded')
          }
        }, 50)
      }
    }

    return () => {
      if (bodyWrapperRef.current) {
        bodyWrapperRef.current.removeEventListener('transitionend', transitionEndHandler)
      }

      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [webinarItem, isDirty])
  const webinarClassNames = classnames('webinar')

  return (
    <div className={webinarClassNames} ref={webinarRef}>
      {webinarItem.webinar.isScheduled && (
        <div className="webinar__image">
          <ImageOrAutoplayVideo
            imageRef={{
              ...webinarItem.webinar.thumbnailImage,
              altText: webinarItem.webinar.title,
            }}
          />
        </div>
      )}
      <div className="webinar__content">
        <div className="webinar__title">
          <Text brand="default" size="heading-5">
            {webinarItem.webinar.title}
          </Text>
        </div>
        <div className="webinar__details">
          <div className="webinar__details__information">
            <button
              className={classnames('webinar__details__cta', {
                'is-expanded': webinarItem.expanded,
              })}
              onClick={() => handleClick(webinarItem.id)}
            >
              {translate('Details')}
              {renderIconSVG(Icon.More)}
            </button>
            {webinarItem.webinar.isScheduled && webinarItem.webinar.zoom && (
              <Text brand="default" size="body">
                {format(webinarItem.webinar.zoom.startTimestamp, 'LLLL dd, h:mm bbb z')}
              </Text>
            )}
          </div>
          <div className={bodyWrapperClassNames} ref={bodyWrapperRef}>
            <div className="webinar__subtitle">
              <Text brand="default" size="body">
                {webinarItem.webinar.subtitle}
              </Text>
            </div>
          </div>
        </div>
        <div className="webinar__cta">
          <CTALink
            text={translate('Learn more')}
            url={webinarItem.webinar.url}
            bgColor={CTATheme.LinkOnlyBlack}
          />
        </div>
      </div>
      <style jsx>{styles}</style>
    </div>
  )
}
